import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Activity, ChevronLeft, ChevronRight } from 'lucide-react';
import { activityApi } from '../services/api';
import type { ActivityLogParams, IActivityLog } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Badge, { getRoleBadgeVariant } from '../components/Badge';

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  LOGIN: { label: 'Hyrje', color: 'bg-green-100 text-green-700' },
  LOGOUT: { label: 'Dalje', color: 'bg-gray-100 text-gray-700' },
  POST_VIEW: { label: 'Shikoi postim', color: 'bg-blue-100 text-blue-700' },
  POST_CREATE: { label: 'Krijoi postim', color: 'bg-purple-100 text-purple-700' },
  POST_EDIT: { label: 'Ndryshoi postim', color: 'bg-yellow-100 text-yellow-700' },
  POST_DELETE: { label: 'Fshiu postim', color: 'bg-red-100 text-red-700' },
  SCREENSHOT_ATTEMPT: { label: 'Screenshot', color: 'bg-red-100 text-red-700' },
  APP_OPEN: { label: 'Hapi app', color: 'bg-green-100 text-green-700' },
  APP_CLOSE: { label: 'Mbylli app', color: 'bg-gray-100 text-gray-700' },
  APP_BACKGROUND: { label: 'App background', color: 'bg-gray-100 text-gray-700' },
  SCREEN_VIEW: { label: 'Shikoi ekran', color: 'bg-blue-100 text-blue-700' },
  CLIPBOARD_COPY: { label: 'Kopjoi', color: 'bg-orange-100 text-orange-700' },
  FILE_DOWNLOAD: { label: 'Shkarkoi file', color: 'bg-indigo-100 text-indigo-700' },
  NOTIFICATION_OPEN: { label: 'Hapi njoftim', color: 'bg-cyan-100 text-cyan-700' },
};

const POST_RELATED_ACTIONS = new Set([
  'POST_VIEW',
  'POST_CREATE',
  'POST_EDIT',
  'POST_DELETE',
  'NOTIFICATION_OPEN',
]);

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('sq-AL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function EventDetails({ log }: { log: IActivityLog }) {
  const meta = log.metadata as Record<string, any> | null | undefined;
  const postId = meta?.postId as string | undefined;
  const postTitle = meta?.postTitle as string | undefined;

  if (postId && POST_RELATED_ACTIONS.has(log.action)) {
    return (
      <Link
        to={`/posts?postId=${postId}`}
        className="text-sm text-barrikadave-600 hover:underline truncate"
        title={postTitle || postId}
      >
        {postTitle || 'Pa titull'}
      </Link>
    );
  }

  if (!meta || Object.keys(meta).length === 0) {
    return <span className="text-sm text-gray-400">-</span>;
  }

  const text = Object.entries(meta)
    .filter(([k]) => k !== 'postId' && k !== 'postTitle')
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ');
  return <span className="text-sm text-gray-500">{text || '-'}</span>;
}

export default function ActivityUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const [page, setPage] = useState(1);

  const params: ActivityLogParams = { userId, page, limit: 50 };

  const { data: logs, isLoading } = useQuery({
    queryKey: ['activity-logs', params],
    queryFn: () => activityApi.getLogs(params),
    enabled: !!userId,
    refetchInterval: 15000,
  });

  const user = logs?.data?.[0]?.user;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/activity"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-barrikadave-600"
        >
          <ArrowLeft className="w-4 h-4" />
          Kthehu te Aktiviteti
        </Link>
      </div>

      {user && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-barrikadave-100 flex items-center justify-center shrink-0">
            <span className="text-barrikadave-600 font-semibold">
              {user.firstName[0]}
              {user.lastName[0]}
            </span>
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {user.firstName} {user.lastName}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={getRoleBadgeVariant(user.role)}>{user.role}</Badge>
              <span className="text-sm text-gray-500">{user.email}</span>
            </div>
          </div>
          {logs?.pagination && (
            <div className="ml-auto text-right">
              <p className="text-2xl font-bold text-gray-900">{logs.pagination.total}</p>
              <p className="text-xs text-gray-500">veprime gjithsej</p>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : !logs?.data?.length ? (
          <div className="text-center py-16 text-gray-400">
            <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nuk ka aktivitete per te shfaqur</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Koha
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Veprimi
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Detajet
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      IP
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.data.map((log) => {
                    const actionInfo = ACTION_LABELS[log.action] || {
                      label: log.action,
                      color: 'bg-gray-100 text-gray-700',
                    };
                    return (
                      <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {formatDate(log.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${actionInfo.color}`}
                          >
                            {actionInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 max-w-[320px]">
                          <EventDetails log={log} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400 whitespace-nowrap">
                          {log.ipAddress || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {logs.pagination && logs.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Faqja {logs.pagination.page} nga {logs.pagination.totalPages} ({logs.pagination.total} gjithsej)
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={logs.pagination.page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    disabled={logs.pagination.page >= logs.pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
